/**
 * The signed-in identity, as the SERVER sees it.
 *
 * Distinct from the GoTrue session the frontend already holds: role and organisation live
 * in `profiles` and are loaded per request so that revoking a role takes effect on the
 * next request rather than the next token refresh. The client cannot derive either from
 * its token.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import { useTheme, type Accent, type Palette } from '@/app/theme';

export type UserRole = 'engineer' | 'auditor' | 'operator' | 'sales' | 'admin';

export interface MeResponse {
  user: {
    id: string;
    email: string;
    fullName: string | null;
    /**
     * The role slugs, joined with `, `. A free string on the server, because an organisation can
     * author one — and since 0060 it is a LIST, because a person holds a set of roles.
     *
     * For everybody on this deployment today that is one word and byte identical to what this
     * field has always carried, which is why it keeps the `UserRole` type and its callers did not
     * move. It is a LABEL. Anything that asks "does this caller hold X" must read `roles` below,
     * and anything that gates must read `permissions` — a comparison against this string answers
     * `false` for a person who holds the role alongside another, which is a silent deny.
     */
    role: UserRole;
    /**
     * The roles' display names, as their rows carry them — joined the same way, in the same
     * order, so `role` and this pair up position for position.
     *
     * THE SERVER'S NAME IS THE NAME. This console used to map the five slugs to labels of its
     * own, and the two disagreed in public: the Access screen rendered `roles.name` —
     * "Administrator" — while Users & access rendered the local map's "Platform admin", for the
     * same role, one click apart. A local map also has nothing at all to say about a role an
     * organisation authored.
     */
    roleName: string;
    /**
     * EVERY ROLE THEY HOLD, as `{slug, name}`, sorted by slug (0060). THE AUTHORITY — `role` and
     * `roleName` above are renderings of it.
     *
     * A screen that draws a chip per role, or asks whether this caller holds Administrator, reads
     * this. Splitting `roleName` on a comma to recover the list is the obvious shortcut and it
     * breaks on the first authored role whose NAME contains one, which is a thing an organisation
     * may type at any time.
     *
     * It is still not what gates a control: `permissions` is the union of these roles' grants
     * with the organisation's ceiling already intersected, and a role can hold a permission the
     * ceiling withholds. Two roles that both grant `alarms.triage` appear once there.
     */
    roles: Array<{ slug: string; name: string }>;
    /**
     * Whether this role narrows to assigned sites.
     *
     * Needed to read `scope` below honestly: an empty site list under `organisation` is an
     * organisation with no sites, and under `site` it is somebody who has not been assigned any.
     * Rendering the same blank screen for both is how the second is diagnosed as the first.
     */
    scopeKind: 'organisation' | 'site';
  };
  organisation: {
    id: string;
    name: string;
    slug: string;
    /**
     * The organisation's remote-control switch (ADR-0014 gate 1), reported as its own fact.
     *
     * NOT for deciding whether to draw a write control — read `can.proposeWrites` for that,
     * which has this already AND-ed in. This is for the one screen that has to state what
     * every OTHER role may do: the roles-and-permissions table, which without it shows
     * `writes.propose` held by Engineer and Admin in an organisation where the switch is off
     * and nobody can write at all.
     */
    remoteControlEnabled: boolean;
    /**
     * Whether the CALLER'S OWN organisation operates this deployment.
     *
     * Read from `/me` rather than inferred from the organisation list, because that list is
     * scoped to the header's selection: switch to a tenant and your own organisation is not in
     * it at all, so a platform administrator stopped looking like one the moment they went to
     * administer somebody. This answer does not move when the header does.
     */
    isPlatform: boolean;
    /**
     * THE ORGANISATION'S LOOK, ALREADY RESOLVED — never null, whatever the column holds.
     *
     * Two of the console's three appearance axes belong to the organisation (0058). The column is
     * nullable and NULL means "inherit the platform default", but this endpoint resolves it
     * server-side because its caller is a page that has to render something. Do NOT re-implement
     * that fallback here: two copies of a default drift in exactly one direction — the platform
     * re-brands, the server's copy moves, and every tenant that never chose keeps rendering the
     * old look because the frontend filled the gap itself.
     *
     * `GET /organisations` serves the RAW nullable pair, for the editor that has to offer a way
     * back to unset. See `Organisation.palette` in `lib/api/tenancy.ts`.
     *
     * Light-or-dark is deliberately absent and always will be: it is personal comfort, not
     * branding, and it lives in `app/theme.ts` and the browser.
     */
    palette: Palette;
    accent: Accent;
  };
  /** For rendering only — every one is enforced server-side by `requireRole`. */
  can: {
    editSites: boolean;
    integrateDevices: boolean;
    editMetrics: boolean;
    manageFindings: boolean;
    /*
     * The remote-control pair (ADR-0014). Both arrive ALREADY AND-ed with the
     * organisation's `remote_control_enabled` gate server-side, so an org with the
     * feature off reads `false` here whatever the role says — the UI never has to
     * consult the org flag separately, and must not try to.
     */
    proposeWrites: boolean;
    approveWrites: boolean;
  };
  /**
   * THE FULL PERMISSION LIST, which this endpoint has been returning all along and which
   * this interface did not declare — so every screen that needed it fetched `/roles`
   * instead, a second request for a list already sitting in the `/me` response.
   *
   * The backend's own note says `can` is kept in its original shape for existing callers
   * and "`permissions` is the full list, which is what new screens should read". Declaring
   * it here is what makes that possible.
   *
   * NOT AND-ed with the organisation's remote-control gate, unlike `can.proposeWrites` —
   * this is the ROLE's permission set. A screen deciding whether to draw a write control
   * must read `can`, which has the gate folded in.
   */
  permissions: string[];
  /**
   * WHICH SITES THIS CALLER REACHES. `null` means unscoped — the whole organisation.
   *
   * `null` and `{ siteIds: [] }` are different answers and must render differently. See
   * `user.scopeKind`.
   */
  scope: { siteIds: string[] } | null;
  /**
   * THE ORGANISATION TREE THIS CALLER REACHES, and which of them they are working in.
   *
   * `organisations` is every organisation they can see — their own plus every descendant,
   * transitively. Flat rows with `parentId`, not a nested structure, because the Access screen
   * already draws one tree from that shape and two representations of one hierarchy would be two
   * things to keep agreeing.
   *
   * `selected` is the WRITE TARGET, not a view filter. Creating a site or inviting a person while
   * a descendant is selected puts the new row in that descendant — which is the only way a
   * sub-organisation ever gets its own sites and people.
   *
   * ── ONE ENTRY MEANS NO SWITCHER ─────────────────────────────────────────────
   * A control that cannot act is hidden, not disabled — the same rule the industry switcher and
   * the remote-control rail follow. An organisation with no children sees no chrome at all, and
   * the switcher appears by itself the day it has one.
   */
  reach: {
    /** The organisation being worked in. Sent back as `X-Lumen-Organisation`. */
    selected: string;
    /** Their own organisation — where they fall back to, and where they always may act. */
    home: string;
    organisations: Array<{ id: string; name: string; slug: string; parentId: string | null }>;
  };
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * READING `/me` IS WHAT PAINTS THE ORGANISATION'S LOOK
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The palette and the accent belong to the organisation (0058) and arrive here already resolved.
 * Something has to take them out of the response and put them on `<html>`, and the choice was
 * between a one-line effect in the shell and this.
 *
 * IT IS HERE BECAUSE THE SHELL IS NOT THE ONLY THING THAT RENDERS. `App.tsx` mounts a pre-auth
 * branch — sign-in, invite, set-password, MFA, locked — outside the shell entirely, and an
 * accepted invite lands on a page that has an identity and no shell around it. An effect in
 * `Shell.tsx` would paint the console and not those.
 *
 * The cost is that this runs once per `useMe()` CALL SITE rather than once per app, and there are
 * a dozen. That is paid for in `applyOrganisation`, which returns immediately when the value has
 * not moved — so after the first paint every other call site is a comparison of two strings, and
 * no subscriber is notified. Cheaper than the bug it removes.
 *
 * The effect deliberately does NOT clear anything on unmount. There is no "no organisation" look
 * to fall back to, and a sign-out that reverted the console to thunder/lime mid-transition would
 * flash the wrong brand on the way out.
 */
function usePaintOrganisation(palette: Palette | undefined, accent: Accent | undefined): void {
  const applyOrganisation = useTheme((s) => s.applyOrganisation);
  useEffect(() => {
    if (palette === undefined || accent === undefined) return;
    applyOrganisation(palette, accent);
  }, [palette, accent, applyOrganisation]);
}

export function useMe() {
  const q = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/me'),
    /*
     * Long but not Infinity, and it is no longer what carries a permission change.
     *
     * This used to be the ONLY thing that did: an admin demoted somebody and the console kept
     * the old capabilities for up to five minutes. `x-lumen-access` now corrects the cache the
     * moment the person makes any request at all (see `lib/api/client.ts`), so this staleness
     * only governs an idle tab — and refetching identity on every navigation is pure waste.
     */
    staleTime: 5 * 60_000,
    /*
     * THE ONE CASE THE DIGEST CANNOT REACH: somebody sitting still. They make no request, so no
     * response carries the new value. Coming back to the tab is the moment they are about to
     * act, and the cheapest place to catch them — the app default is `false` and stays false,
     * because this is the one query where the answer can change while nothing else does.
     */
    refetchOnWindowFocus: true,
  });
  usePaintOrganisation(q.data?.organisation.palette, q.data?.organisation.accent);
  return q;
}

/**
 * Last-resort labels for the five system slugs.
 *
 * ONLY FOR CALLERS THAT HOLD A SLUG AND NOT A ROW. Anything that has the role's row must render
 * `roles.name`, which is what the server and the Access screen already show — this map exists so
 * a slug never renders raw, not as a second naming authority. It said "Platform admin" where the
 * database says "Administrator", and the two shipped on screens one click apart.
 *
 * It has nothing to say about a role an organisation authored, which is the other reason not to
 * reach for it: `titleCase` at least renders `site-engineer` as "Site engineer".
 */
const SYSTEM_ROLE_LABEL: Record<string, string> = {
  admin: 'Administrator',
  engineer: 'Engineer',
  auditor: 'Auditor',
  operator: 'Operator',
  sales: 'Sales',
};

/**
 * A role's display name.
 *
 * Pass the server's `name` whenever you have it — from `/me`'s `user.roleName`, or from a
 * `RoleDetail` row. The slug is the fallback, not the source.
 */
export function roleLabel(role: string, serverName?: string | null): string {
  if (serverName != null && serverName.trim() !== '') return serverName;
  const known = SYSTEM_ROLE_LABEL[role];
  if (known !== undefined) return known;
  /* An authored role: `site-engineer` reads as "Site engineer" rather than as an identifier. */
  const words = role.replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** What the role can actually do, in one line, for the account menu. */
const ROLE_SCOPE: Record<string, string> = {
  admin: 'Full access, including device integration and parameter configuration.',
  engineer: 'Can edit sites, integrate devices and configure parameters.',
  auditor: 'Read-only, plus finding remediation and sign-off.',
  operator: 'Read-only monitoring.',
  sales: 'Read-only.',
};

export function roleScope(role: string): string {
  return ROLE_SCOPE[role] ?? '';
}
